/*
 * 戒导航 · 上传模块（jiedh/upload）—— 自研 Tiptap 节点 uplImagePreview
 * =============================================================================
 * 设计依据：doc/输入框富文本编辑器设计方案.md §8.4（服务端渲染 HTML）、§8.5（方案 B）
 *
 * 【为什么必须自研这个节点】
 *   fof/upload 的 image-preview 模板产出的是「自闭合 BBCode」（单行、无闭合标签）：
 *     [upl-image-preview uuid=… url=… alt=… thumbnail_url=…]
 *   而 fof/rich-text 的富文本编辑器（Tiptap）不认识这个标签，
 *   于是 TiptapEditorDriver.insertBetween() 里的 parser.parse(text) 降级成了纯文本，
 *   编辑器里就出现了 BBCode 原文（§8.1 现象 / §8.2 根因链）。
 *   本节点把这个标签变成「内联原子节点」，从而：
 *     · 编辑器内直接渲染成图片（不再出现 BBCode 原文）；
 *     · 序列化时**原样还原** BBCode（落库格式不变，content-guard 的第 ⑤ 条拦截继续生效，§8.6）。
 *
 * 【硬约束一：必须复用 fof/rich-text 的 Tiptap / ProseMirror 实例】
 *   绝对不能在本包内 `import { Node } from '@tiptap/core'` —— 那会打出第二份 @tiptap/core
 *   与第二份 @tiptap/pm/model，导致 schema 校验失败、装饰器失效等极难排查的问题。
 *   正确做法：运行期从 flarum.reg 取回 fof/rich-text 已经注册、并被重新导出的 primitives，
 *   它与编辑器内部使用的是同一个模块实例，见：
 *     vendor/fof/rich-text/js/src/common/tiptap/tiptap.ts（源码，含同样的注释）
 *     vendor/fof/rich-text/js/dist/common/tiptap/TiptapEditorDriver.js 里
 *       flarum.reg.add("fof-rich-text","common/tiptap/tiptap",{Node:…,Mark:…,Extension:…})
 *
 * 【硬约束二：属性名必须与 bbcode.ts 的 UplImagePreviewAttrs 一致】
 *   buildTokens 的 getAttrs 返回的就是这份属性对象，键名对不上会导致 addNode 用
 *   createAndFill 建节点时属性丢失（prosemirror 的 createAndFill 会静默丢弃不合规节点）。
 *
 * 【为什么 renderHTML 只输出 <img>、不包外层 <a>】
 *   服务端（image-preview.blade.php）渲染的是 <a href><img></a>。
 *   编辑器内的 DOM 若也带 <a href>，编辑正文时点击图片会直接跳转离开编辑器，
 *   属于明显的体验缺陷；因此编辑器内只渲染 <img>（与内置 image 节点一致），
 *   而原图地址 url 仍完整保存在节点属性里、并原样序列化回 BBCode，不会丢失。
 *   解析方向（parseHTML）依然兼容服务端的 <a><img></a> 结构：url 通过 closest('a') 回退读取。
 *
 * 【那编辑器里的「点击放大」怎么办？—— 由本文件的 ProseMirror 插件负责】
 *   不包 <a> 意味着编辑器内没有天然的点击行为，而 less/forum.less 的 cursor: zoom-in
 *   又确实作用在编辑器内的图片上（fof/rich-text 把 Post-body 类加到了 .ProseMirror 元素上，
 *   见 vendor/fof/rich-text/js/src/common/tiptap/TiptapEditorDriver.ts 第 95-96 行）。
 *   于是补一个 ProseMirror 插件：点击图片时调用 imageZoom.ts 的原生浮层展示原图，
 *   并额外输出 data-url 供点击时取原图地址（url 属性也优先读 data-url，
 *   这样「编辑器内复制→粘贴」也不会把原图降级成缩略图）。
 *
 *   为什么用插件的 view 字段挂「原生捕获阶段监听」，而不用 handleDOMEvents.click：
 *     · handleDOMEvents 会把所有插件的同类回调合并成一条链，某个插件返回 true 就中断，
 *       本节点在扩展列表里排在内置 image 之后（见 registerUplImagePreview.ts 关于排序的注释），
 *       一旦内置/未来的扩展在 click 上抢返回 true，放大功能就会静默失效；
 *     · 捕获阶段挂在 view.dom 上的原生监听不受插件注册顺序影响，也不会被任何
 *       stopPropagation 挡在祖先节点之外，行为稳定可预期；
 *     · 代价（自己负责摘监听）由 PM 的 view.destroy() 承担，编辑器销毁时自动清理。
 *
 *   全程不调用 preventDefault / stopPropagation：ProseMirror 继续做它自己的原子节点选中，
 *   蓝色选中框、拖拽排序、方向键删图这些行为全部保持原样。
 *   再用「按下与松开之间的位移」把拖拽排除掉（见 CLICK_MOVE_TOLERANCE），
 *   避免拖完图片手一松开就弹出放大层。
 */
import { UPL_IMAGE_PREVIEW_NODE } from './bbcode';
import { openImageZoom } from './imageZoom';

/** 编辑器内图片的 class（与 less/forum.less、服务端模板保持同一字面量）。 */
const PREVIEW_IMG_CLASS = 'FoFUpload--Upl-Image-Preview';

/**
 * mousedown 到 click 之间允许的最大位移（像素）。
 * 超过它就认定这是一次「拖拽图片 / 框选文字」而不是点击，避免拖完手一松开就弹出放大层。
 */
const CLICK_MOVE_TOLERANCE = 6;

/**
 * 从事件目标往上找编辑器内的图片预览节点（<img class="FoFUpload--Upl-Image-Preview">）。
 * 用 closest 而不是直接判 e.target，是为了兼容图片被额外包一层 span 的情况。
 */
function previewImageFrom(target) {
  if (!(target instanceof Element)) return null;
  const img = target.closest(`img.${PREVIEW_IMG_CLASS}`);
  return img instanceof HTMLImageElement ? img : null;
}

/**
 * 取这张图对应的「原图地址」。
 * 优先级：节点属性 url（最权威，来自 [upl-image-preview url=…]）
 *       → <img data-url>（属性拿不到时的兜底，比如 DOM 被外部改写过）
 *       → <img src>（最后兜底，最差也就是缩略图，总好过点了没反应）。
 */
function resolveOriginalUrl(view, img) {
  try {
    const pos = view.posAtDOM(img, 0);
    const node = typeof pos === 'number' && pos >= 0 ? view.state.doc.nodeAt(pos) : null;
    if (node && node.type && node.type.name === UPL_IMAGE_PREVIEW_NODE) {
      const url = node.attrs.url;
      if (url) return url;
    }
  } catch (error) {
    // posAtDOM 在节点尚未同步到 doc 时会抛错，忽略即可，继续走 DOM 兜底
    console.warn('[jiedh/upload] 读取 uplImagePreview 节点属性失败，改用 DOM 属性兜底：', error);
  }
  return img.getAttribute('data-url') || img.getAttribute('src') || '';
}

/** 取这张图的说明文字（文件名 slug），只用于放大层下方那一行小字。 */
function resolveCaption(view, img) {
  try {
    const pos = view.posAtDOM(img, 0);
    const node = typeof pos === 'number' && pos >= 0 ? view.state.doc.nodeAt(pos) : null;
    if (node && node.type && node.type.name === UPL_IMAGE_PREVIEW_NODE) {
      const alt = node.attrs.alt;
      if (alt) return alt;
    }
  } catch {
    /* 同上：拿不到就不显示说明行 */
  }
  return img.getAttribute('alt') || '';
}

/** fof/rich-text 重新导出 Tiptap primitives 时的注册坐标。 */
const RICH_TEXT_NAMESPACE = 'fof-rich-text';
const RICH_TEXT_TIPTAP_ID = 'common/tiptap/tiptap';

/**
 * 从注册表取回 fof/rich-text 的 Tiptap primitives。
 *
 * 为什么是运行期取、而不是顶层 import：
 *   TiptapEditorDriver 及其依赖被打在 fof/rich-text 的异步 chunk 里，只有在编辑器真正构建时
 *   才保证已注册。本函数只在 buildExtensions 回调里被调用，那时模块必然已就绪。
 *
 * @returns { Node, Mark, Extension, … } | null —— 取不到时返回 null（调用方降级跳过，绝不让编辑器崩）。
 */
function getTiptapPrimitives() {
  const primitives = flarum.reg.get(RICH_TEXT_NAMESPACE, RICH_TEXT_TIPTAP_ID);
  if (!primitives || !primitives.Node || typeof primitives.Node.create !== 'function') {
    console.warn('[jiedh/upload] 未能从 flarum.reg 取到 fof-rich-text 的 Tiptap primitives' + `（${RICH_TEXT_NAMESPACE} / ${RICH_TEXT_TIPTAP_ID}），` + 'upl-image-preview 的富文本支持已跳过，上传图片将退回「显示 BBCode 原文」的旧行为。');
    return null;
  }
  return primitives;
}

/**
 * 创建 uplImagePreview 内联原子节点。
 * 每次编辑器构建都会调用一次（ItemList 是新建的），因此这里每次都新建节点实例。
 */
export function createUplImagePreviewNode() {
  const primitives = getTiptapPrimitives();
  if (!primitives) return null;
  const {
    Node,
    Plugin,
    PluginKey
  } = primitives;
  return Node.create({
    name: UPL_IMAGE_PREVIEW_NODE,
    /* ---- schema 定义 ---------------------------------------------------- */
    // 内联 + 原子：整张图在正文里作为一个不可拆分的整体，光标只能落在它两侧。
    inline: true,
    group: 'inline',
    atom: true,
    draggable: true,
    selectable: true,
    /* ---- 属性（键名必须与 bbcode.ts 的 UplImagePreviewAttrs 完全一致）---- */
    addAttributes() {
      return {
        /** 媒体记录 uuid —— 对应服务端 <img data-id="…"> */
        uuid: {
          default: null,
          parseHTML: element => element.getAttribute('data-id')
        },
        /**
         * 原图地址。
         * 读取顺序：data-url（编辑器自身 DOM，见 renderHTML）
         *           → 外层 <a href>（服务端 image-preview.blade.php 的结构）
         *           → <img src>（都没有时退化成缩略图）。
         * 把 data-url 放在第一位是关键：编辑器内「复制图片 → 粘贴」走的是 renderHTML 产出的
         * DOM，若不优先读 data-url，原图地址会被 src 里的缩略图覆盖，粘贴出来的图永久降级。
         */
        url: {
          default: null,
          parseHTML: element => element.getAttribute('data-url') || element.closest('a')?.getAttribute('href') || element.getAttribute('src')
        },
        /** 文件名（slug）—— 对应服务端 <img alt="…" title="…"> */
        alt: {
          default: null,
          parseHTML: element => element.getAttribute('alt')
        },
        /** 缩略图地址 —— 对应服务端 <img src="…">；缺省时由 bbcode.ts 回退到 url */
        thumbnailUrl: {
          default: null,
          parseHTML: element => element.getAttribute('src')
        }
      };
    },
    /* ---- HTML 解析（用于粘贴 / 存量 HTML 内容）-------------------------- */
    parseHTML() {
      return [{
        /*
         * data-id 是 fof/upload 图片预览独有的标记（见 image-preview.blade.php），
         * 内置的 image 节点规则是 img[src]，不会带 data-id —— 用 data-id 把两者区分开。
         * priority 默认 50，这里给 60 以保证在后端渲染出的 <a><img data-id …></a> 上
         * 优先命中本节点，而不是被内置 image 节点抢走（§8.9 验收 #6：编辑存量老帖）。
         */
        tag: 'img[data-id]',
        priority: 60
      }];
    },
    /* ---- HTML 渲染（编辑器内 DOM）-------------------------------------- */
    renderHTML(_ref) {
      let {
        node
      } = _ref;
      const attrs = node.attrs;
      const url = attrs.url ?? '';
      return ['img', {
        class: PREVIEW_IMG_CLASS,
        // src 是缩略图（缺省时回退原图），data-url 才是原图 —— 点击放大与复制粘贴都靠它
        src: attrs.thumbnailUrl || url,
        'data-url': url,
        title: attrs.alt ?? '',
        alt: attrs.alt ?? '',
        'data-id': attrs.uuid ?? '',
        loading: 'lazy'
      }];
    },
    /* ---- 「点击图片原地放大」-------------------------------------------- */
    /**
     * 挂一个只负责「看图」的 ProseMirror 插件。
     *
     * 实现要点（也是为什么不写成 handleDOMEvents，见文件头注释）：
     *   · 用插件的 view 字段，在编辑器 DOM 的**捕获阶段**挂原生 mousedown / click 监听；
     *   · 两个监听器都**不 preventDefault、不 stopPropagation**，
     *     所以原子节点选中框、拖拽、方向键删除等编辑器原生行为完全不受影响；
     *   · destroy() 里摘监听，编辑器销毁时不留残余（PM 保证每个 editor 实例只 view() 一次）。
     */
    addProseMirrorPlugins() {
      // Plugin / PluginKey 由 fof/rich-text 重新导出（vendor/.../tiptap/tiptap.ts），
      // 缺任何一件就放弃放大功能，绝不让编辑器构建失败。
      if (typeof Plugin !== 'function' || typeof PluginKey !== 'function') {
        console.warn('[jiedh/upload] Tiptap primitives 中缺少 Plugin / PluginKey，编辑器内「点击图片放大」已跳过。');
        return [];
      }
      return [new Plugin({
        key: new PluginKey('jiedh-upload-image-zoom'),
        // 监听器一律在 view 回调内部定义：这样 view（编辑器实例）走参数闭包，
        // 不依赖 this，也保证「每个编辑器实例一份状态、一份监听」。
        view: view => {
          const dom = view.dom;

          // 按下时的坐标与目标图片（用于区分「点击」和「拖拽后松手」）
          let pressedAt = null;
          const handleMouseDown = event => {
            // 只认左键；右键交给浏览器菜单
            if (event.button !== 0) {
              pressedAt = null;
              return;
            }
            const img = previewImageFrom(event.target);
            pressedAt = img ? {
              x: event.clientX,
              y: event.clientY,
              img
            } : null;
          };
          const handleClick = event => {
            const start = pressedAt;
            pressedAt = null;
            const img = previewImageFrom(event.target);
            if (!img) return;

            // 位移超阈值 ⇒ 这是拖拽图片 / 框选文字后松手，不是「点击」，不弹层
            if (start && start.img === img) {
              const moved = Math.max(Math.abs(event.clientX - start.x), Math.abs(event.clientY - start.y));
              if (moved > CLICK_MOVE_TOLERANCE) return;
            }
            openImageZoom(resolveOriginalUrl(view, img), resolveCaption(view, img));

            // 注意：这里刻意不阻断事件，让 ProseMirror 照常完成它自己的节点选中。
          };
          dom.addEventListener('mousedown', handleMouseDown, true);
          dom.addEventListener('click', handleClick, true);
          return {
            destroy() {
              dom.removeEventListener('mousedown', handleMouseDown, true);
              dom.removeEventListener('click', handleClick, true);
            }
          };
        }
      })];
    }
  });
}